import { makeUUID } from '../utils/refi-mfe-uuid.js';

const _subscriptions = new Map();

const _worker = Promise.resolve()
  .then(() => new Worker(new URL('./http.worker.js', import.meta.url)))
  .then(worker => {
    // eslint-disable-next-line no-param-reassign
    worker.onmessage = ({ data: { key, response, error } }) => {
      const { resolve, reject } = _subscriptions.get(key);
      if (error) {
        reject(error);
      } else {
        resolve(new Response(response));
      }
      _subscriptions.delete(key);
    };
    return worker;
  });

/**
 * Provides extra functionality for making fetch requests.
 * @see window.fetch
 */
class HttpClient {
  constructor() {
    this._config = {
      retry: 1,
      worker: false,
    };
  }

  /**
   * Wraps window.fetch
   * @param {RequestInfo} input
   * @param {RequestInit} [init]
   * @returns {Promise<Response>}
   * @see window.fetch
   */
  async fetch(input, init) {
    const { input: finalInput, init: finalInit } = this._applyDefaults(input, init);

    const imp = this._config.worker ? this._fetch.bind(this) : window.fetch;
    return this._retry(() => imp(finalInput, finalInit), this._config.retry);
  }

  withRetry(n) {
    const client = new HttpClient();
    client._config = { ...this._config, retry: n };
    return client;
  }

  withWorkerSupport(flag) {
    const client = new HttpClient();
    client._config = { ...this._config, worker: flag };
    return client;
  }

  /**
   * Custom fetch that offloads the fetch to a worker thread
   * @param input
   * @param init
   * @returns {Promise<Response>}
   * @private
   */
  async _fetch(input, init) {
    const uuid = HttpClient._makeUUID();
    const worker = await _worker;
    return new Promise((resolve, reject) => {
      worker.postMessage({
        key: uuid,
        input,
        init,
      });

      _subscriptions.set(uuid, { resolve, reject });
    });
  }

  static _makeUUID() {
    return makeUUID();
  }

  async _retry(fn, n) {
    try {
      return await fn();
    } catch (e) {
      const c = n - 1;
      if (c < 1) {
        throw e;
      } else {
        return this._retry(fn, c);
      }
    }
  }

  async withCustomTimeout(request, timeout = 10000) {
    const controller = new AbortController();
    const { signal } = controller;
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    const result = await Promise.race([
      request,
      new Promise((_, reject) => {
        signal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          reject(new Error('Request timed out'));
        });
      }),
    ]);
    clearTimeout(timeoutId);
    return result;
  }

  _applyDefaults(input, init) {
    const defaultInit = {
      headers: {
        'Client-Correlation-Id': HttpClient._makeUUID(),
      },
    };

    const finalizeInput = () => {
      if (input instanceof Request) {
        const f = new Request(input);
        f.headers.append('Client-Correlation-Id', HttpClient._makeUUID());
        return f;
      }

      return input;
    };

    const finalizeInit = () => {
      if (init) {
        const h = {
          ...defaultInit.headers,
          ...init.headers,
        };
        return {
          ...init,
          headers: h,
        };
      }
      return init;
    };

    return {
      input: finalizeInput(),
      init: finalizeInit(),
    };
  }
}

const RefiHttpClient = HttpClient;

export { RefiHttpClient };
